import { Button, Modal, TextButton } from '@safetyhub/ui';
import {
  SiteAssignment,
  OrganizationMember,
  OrganizationInvitation,
  OWNER_LABEL,
} from '../../../../lib/organizations';
import { MemberSelect } from '../../../../components/MemberSelect';
import { AssigneeDetailModal } from '../../../../components/AssigneeDetailModal';
import { AssignmentType } from '../../../../lib/organizations';
import { ChevronLeftIcon, ExclamationIcon } from './Icons';
import { SiteDetailState, SiteDetailHandlers, SiteDetailPermissions } from '../types';

/** 초대 폼 id — 제출 버튼이 base 푸터(폼 밖)에 있어 `form` 속성으로 잇는다. */
const SITE_INVITE_FORM_ID = 'site-assign-invite-form';

interface AssignModalProps {
  state: SiteDetailState;
  handlers: SiteDetailHandlers;
  permissions: SiteDetailPermissions;
  assignments: SiteAssignment[];
}

/**
 * 담당자 추가 모달
 */
export function AssignModal({
  state,
  handlers,
  permissions,
  assignments,
}: AssignModalProps) {
  const {
    showAssignModal,
    selectedMemberId,
    setSelectedMemberId,
    isAssigning,
    assignMode,
    setAssignMode,
    setPrimaryOnAssign,
    setSetPrimaryOnAssign,
    inviteIdentifier,
    setInviteIdentifier,
    invitePhone,
    setInvitePhone,
    inviteAsOwner,
    setInviteAsOwner,
    inviteRoleKeys,
    toggleInviteRoleKey,
    inviteRoleOptions,
    isLoadingInviteRoles,
    isInviting,
  } = state;

  const { handleAssign, handleInvite, closeAssignModal } = handlers;
  // 모달 안의 두 경로는 **서버 게이트가 다르다** — 멤버 선택은 배정(`site.assign` ∧ owner, 그래서 이 모달을 여는
  // 버튼이 `canAssign`), 「+ 새 사용자 초대」는 `member.invite`(organizations.controller inviteMember)다.
  // 소유자는 전체 키를 가지므로 오늘은 늘 true지만, 축을 선언해 두지 않으면 배정 축이 넓어지는 날
  // 이 초대 입구가 조용히 403을 내는 자리가 된다(그 403은 모달 안 토스트로만 보여 더 나쁘다).
  const { unassignedMembers, canInvite } = permissions;

  if (!showAssignModal) return null;

  // 추가·초대 진행 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전엔 취소 버튼만 disabled였고
  // 오버레이 클릭은 처리 중에도 닫았다.
  const isBusy = isAssigning || isInviting;

  return (
    <Modal
      open
      onClose={closeAssignModal}
      title="담당자 추가"
      size="md"
      closeOnOverlay={!isBusy}
      closeOnEsc={!isBusy}
      showCloseButton={!isBusy}
      closeLocked={isBusy}
      // 본문 스크롤은 **초대 모드에서만** 켠다. 선택 모드의 `MemberSelect` 목록은 절대배치(`absolute`)라
      // 본문 `overflow-y-auto` 경계에 새로 잘린다(base `bodyScroll` docblock ②) — 그 모드는 입력이 둘뿐이라
      // 패널이 뷰포트를 넘지 않는다. 초대 모드는 입력·역할 목록으로 길어질 수 있고 절대배치 자식이 없다.
      bodyScroll={assignMode === 'invite'}
      footer={
        assignMode === 'select' ? (
          <>
            <Button variant="secondary" onClick={closeAssignModal} disabled={isAssigning}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleAssign}
              disabled={isAssigning || !selectedMemberId}
            >
              {isAssigning ? '추가 중...' : '추가'}
            </Button>
          </>
        ) : (
          <>
            <TextButton
              tone="neutral"
              onClick={() => setAssignMode('select')}
              className="mr-auto inline-flex items-center gap-1 text-sm"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              뒤로
            </TextButton>
            <Button variant="secondary" onClick={closeAssignModal} disabled={isInviting}>
              취소
            </Button>
            {/* 푸터는 base가 본문과 **형제**로 그리므로 `<form>` 밖이다 — `form` 속성으로 제출을 잇는다
                (web 어댑터 `components/Modal.tsx`의 footer docblock). */}
            <Button
              variant="primary"
              type="submit"
              form={SITE_INVITE_FORM_ID}
              disabled={isInviting || (!inviteIdentifier.trim() && !invitePhone.trim())}
            >
              {isInviting ? '초대 중...' : '초대'}
            </Button>
          </>
        )
      }
    >
      {assignMode === 'select' ? (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-700 mb-1">
              멤버 선택
            </label>
            <MemberSelect
              members={unassignedMembers}
              value={selectedMemberId}
              onChange={setSelectedMemberId}
              placeholder="선택하세요"
              showInviteOption={canInvite}
              onInviteClick={() => {
                setAssignMode('invite');
                setSelectedMemberId('');
              }}
            />
          </div>

          {assignments.length > 0 && (
            <div className="mb-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={setPrimaryOnAssign}
                  onChange={(e) => setSetPrimaryOnAssign(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-brand-600 border-line rounded focus:ring-brand-500"
                />
                <div>
                  <span className="text-sm text-ink-700">정 담당자로 지정</span>
                  <p className="text-xs text-ink-500">
                    기존 정 담당자는 부 담당자로 변경됩니다
                  </p>
                </div>
              </label>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-ink-500 mb-4">
            새 멤버를 초대하면 수락 시 자동으로 이 현장에 할당됩니다.
          </p>

          <form
            id={SITE_INVITE_FORM_ID}
            onSubmit={(e) => {
              e.preventDefault();
              handleInvite();
            }}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-700 mb-1">
                이메일 또는 아이디
              </label>
              <input
                type="text"
                value={inviteIdentifier}
                onChange={(e) => setInviteIdentifier(e.target.value)}
                placeholder="user@example.com 또는 user123"
                className="block w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="mt-1 text-xs text-ink-500">
                @가 포함되면 이메일, 없으면 아이디로 처리됩니다
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-700 mb-1">
                전화번호 <span className="text-ink-400 font-normal">(선택)</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={invitePhone}
                onChange={(e) =>
                  setInvitePhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))
                }
                placeholder="01012345678"
                maxLength={11}
                className="block w-full px-3 py-2 border border-line rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="mt-1 text-xs text-ink-500">SMS 초대 발송용 (숫자만 입력)</p>
            </div>

            {/* 역할 — 멤버 관리 초대 모달과 같은 축(P7-b-2): 「소유자로 초대」 토글 + 조직 역할 멀티셀렉트(roleKeys, 기본 member) */}
            <fieldset>
              <legend className="block text-sm font-medium text-ink-700 mb-2">역할</legend>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  id="site-invite-as-owner"
                  checked={inviteAsOwner}
                  onChange={(e) => setInviteAsOwner(e.target.checked)}
                  className="h-4 w-4 rounded border-line text-brand-600"
                />
                <span className="ml-2 text-sm text-ink-700">{OWNER_LABEL}로 초대 (모든 권한)</span>
              </label>
              {!inviteAsOwner && (
                <div className="mt-2">
                  {isLoadingInviteRoles ? (
                    <p className="text-xs text-ink-500">역할 목록을 불러오는 중...</p>
                  ) : inviteRoleOptions.length === 0 ? (
                    <p className="text-xs text-ink-500">
                      일반 멤버로 초대됩니다. 역할은 조직 관리 &gt; 역할·선임에서 나중에 배정할 수 있습니다.
                    </p>
                  ) : (
                    <ul className="max-h-40 overflow-y-auto space-y-1 rounded-control border border-line p-2" aria-label="초대 역할">
                      {inviteRoleOptions.map((role) => (
                        <li key={role.key}>
                          <label className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={inviteRoleKeys.includes(role.key)}
                              onChange={() => toggleInviteRoleKey(role.key)}
                              className="mt-0.5 h-4 w-4 rounded border-line text-brand-600"
                            />
                            <span className="text-sm text-ink-700">
                              {role.name}
                              {role.description && (
                                <span className="block text-xs text-ink-500">{role.description}</span>
                              )}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </fieldset>
          </form>
        </>
      )}
    </Modal>
  );
}

interface UnassignModalProps {
  unassignTarget: SiteAssignment | null;
  setUnassignTarget: (target: SiteAssignment | null) => void;
  isUnassigning: boolean;
  handleUnassign: () => Promise<void>;
}

/**
 * 할당 해제 확인 모달
 */
export function UnassignModal({
  unassignTarget,
  setUnassignTarget,
  isUnassigning,
  handleUnassign,
}: UnassignModalProps) {
  if (!unassignTarget) return null;

  // 해제 진행 중에는 닫는 길을 막는다(종전엔 취소 버튼만 disabled였다).
  return (
    <Modal
      open
      onClose={() => setUnassignTarget(null)}
      title="담당자 해제"
      size="md"
      closeOnOverlay={!isUnassigning}
      closeOnEsc={!isUnassigning}
      showCloseButton={!isUnassigning}
      closeLocked={isUnassigning}
      footer={
        <>
          <Button variant="secondary" onClick={() => setUnassignTarget(null)} disabled={isUnassigning}>
            취소
          </Button>
          {/* 되돌릴 수 있는 경고성 액션(배정 해제) — 노랑 의미색을 warning으로 보존. */}
          <Button variant="warning" onClick={handleUnassign} disabled={isUnassigning}>
            {isUnassigning ? '해제 중...' : '해제'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col sm:flex-row sm:items-start">
        <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-yellow-100 mx-auto sm:mx-0">
          <ExclamationIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
        </div>
        <p className="mt-3 sm:mt-0 sm:ml-4 text-center sm:text-left text-sm text-ink-500 sm:self-center">
          이 담당자를 현장에서 해제하시겠습니까?
        </p>
      </div>
    </Modal>
  );
}

interface InvitationCancelModalProps {
  invitationCancelTarget: OrganizationInvitation | null;
  setInvitationCancelTarget: (target: OrganizationInvitation | null) => void;
  isCancellingInvitation: boolean;
  handleCancelInvitation: () => Promise<void>;
}

/**
 * 초대 취소 확인 모달
 */
export function InvitationCancelModal({
  invitationCancelTarget,
  setInvitationCancelTarget,
  isCancellingInvitation,
  handleCancelInvitation,
}: InvitationCancelModalProps) {
  if (!invitationCancelTarget) return null;

  // 취소 진행 중에는 닫는 길을 막는다(종전엔 닫기 버튼만 disabled였다).
  return (
    <Modal
      open
      onClose={() => setInvitationCancelTarget(null)}
      title="초대 취소"
      size="md"
      closeOnOverlay={!isCancellingInvitation}
      closeOnEsc={!isCancellingInvitation}
      showCloseButton={!isCancellingInvitation}
      closeLocked={isCancellingInvitation}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => setInvitationCancelTarget(null)}
            disabled={isCancellingInvitation}
          >
            닫기
          </Button>
          <Button variant="danger" onClick={handleCancelInvitation} disabled={isCancellingInvitation}>
            {isCancellingInvitation ? '취소 중...' : '초대 취소'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col sm:flex-row sm:items-start">
        <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-yellow-100 mx-auto sm:mx-0">
          <ExclamationIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
        </div>
        <p className="mt-3 sm:mt-0 sm:ml-4 text-center sm:text-left text-sm text-ink-500">
          <strong>
            {invitationCancelTarget.email ||
              invitationCancelTarget.phone ||
              (invitationCancelTarget.username && `@${invitationCancelTarget.username}`)}
          </strong>
          에 대한 초대를 취소하시겠습니까?
          <br />
          취소된 초대 링크는 더 이상 사용할 수 없습니다.
        </p>
      </div>
    </Modal>
  );
}

interface AssigneeDetailModalWrapperProps {
  selectedAssignment: SiteAssignment | null;
  setSelectedAssignment: (assignment: SiteAssignment | null) => void;
  setUnassignTarget: (target: SiteAssignment | null) => void;
  members: OrganizationMember[];
  /** 담당자 **유형 변경**(`site.assign`) — 해제 축과 다르다(모달 prop doc 참조). */
  canChangeAssignmentType: boolean;
  /** 할당 **해제**(서버 `unassignMember` isOwnerMember) — 소유권 성질. */
  canUnassign: boolean;
  /** 벽의 「본인」 갈래 — 보는 사람의 CompanyMember.id. */
  viewerMemberId: string | undefined;
  /** 벽의 소유자 갈래 — 소유권 성질. */
  isOwner: boolean;
  /** 공석 충원의 자기 승격에서 쓰는 `site.manage`. */
  canManageSite: boolean;
  /** 그 현장의 배정 전량 — 현 정 담당자 판정(공석인가 / 그 사람이 나인가). */
  assignments: SiteAssignment[];
  handleUpdateAssignmentType: (assignmentType: AssignmentType) => Promise<void>;
}

/**
 * 담당자 상세 모달 래퍼 — 데스크톱·모바일 공용(둘은 `setSelectedAssignment`만 하고 렌더는 여기 한 번).
 *
 * 정 담당자 지정·교체·강등 게이트는 모달이 `lib/sitePrimaryDesignation.ts`로 판정한다
 * (결정 기록 `docs/decisions/2026-09-26-primary-assignee-designation-wall.md`).
 */
export function AssigneeDetailModalWrapper({
  selectedAssignment,
  setSelectedAssignment,
  setUnassignTarget,
  members,
  canChangeAssignmentType,
  canUnassign,
  viewerMemberId,
  isOwner,
  canManageSite,
  assignments,
  handleUpdateAssignmentType,
}: AssigneeDetailModalWrapperProps) {
  if (!selectedAssignment) return null;

  const member = members.find((m) => m.id === selectedAssignment.memberId);
  if (!member) return null;

  return (
    <AssigneeDetailModal
      assignment={selectedAssignment}
      member={member}
      canChangeAssignmentType={canChangeAssignmentType}
      canUnassign={canUnassign}
      viewerMemberId={viewerMemberId}
      isOwner={isOwner}
      canManageSite={canManageSite}
      assignments={assignments}
      onClose={() => setSelectedAssignment(null)}
      onUpdateType={handleUpdateAssignmentType}
      onUnassign={() => {
        setUnassignTarget(selectedAssignment);
        setSelectedAssignment(null);
      }}
    />
  );
}
